import { ProjectType } from '../gql-gen/dashboard/graphql'
import { EntryType, QuestionKind, QuestionType, Row } from './chart'
import { QuotaResults } from './quotaResults'

export interface ResponseOption {
  id: string
  text: string
  position: number
  completes: number | undefined
}

export interface WeightingScheme {
  isCustomWeighting?: boolean
  questions: string[] | null
}

export interface FilterOption {
  id: string
  matrixTitleId?: string
  text: string
  position: number
  samplesCollected: number
  responseOptions: ResponseOption[]
  questionKind: QuestionKind
}

export interface SelectedFilter {
  matrixTitleId?: string
  forkId?: string
  questionId?: string
  // Only set in the combined (MMv2) view: disambiguates a questionId that the
  // combined filter panel legitimately lists twice — once per market cluster —
  // because the two markets' response option sets are disjoint (e.g. Region NL
  // vs Region BE). Undefined for single-market filters and shared combined
  // questions, where questionId alone is already unique.
  marketLabel?: string | null
  responseOptionIds?: string[]
  branchNumbers?: string[]
}

export interface ProjectMetadata {
  projectName: string
  projectType: ProjectType
  country: string
  completes: number
  fieldwork: {
    startDate: string
    endDate: string
  }
  quotas: QuotaResults[]
}

export interface SearchableFilterOption {
  id: string // for matrix: matrixTitleId, question, ranked: questionId
  text: string // for matrix: matrixTitle, question, ranked: questionText
  entryPosition: number
  contextPosition: number // <-- starts with 0 but should start with 1
  questionType: QuestionType
  entryType: EntryType
  questionKind: QuestionKind
  isMultipleChoice: boolean
  samplesWanted: number
  samplesCollected: number
  samplesCollectedFiltered: number
  rows: Row[]
}

export interface ForkFilter {
  forkId: string
  forkName: string
  branches: {
    branchNumber: number
    branchLabel: string
  }[]
}

// New API Response Models for Analytics
export enum FrontendQuestionKind {
  Audience = 'Audience',
  Questionnaire = 'Questionnaire',
  None = 'None'
}

// Response option for regular questions
export interface FilterResponseOption {
  id: string
  text: string
  position: number
  nCompletes?: number | null // Number of respondents who selected this option
  nWeightedCompletes?: number | null // Weighted completes, if applicable
}

// Branch option for fork filters
export interface BranchOption {
  id: string
  text: string
  position: number
  // Note: No nCompletes field for forks
}

// New main filter option (for questions)
export interface NewFilterOption {
  questionId: string // UUID as string
  matrixTitleId?: string | null // UUID as string, only for matrix rows
  // Combined (MMv2) view only: country code of this question's market cluster
  // when a questionId is split into separate per-market cards. Null for
  // single-survey filters and shared (unsplit) combined questions.
  marketLabel?: string | null
  text: string // Full display text (for matrix: 'Matrix Title - Row Text')
  matrixContextPosition?: number | null // Only for matrix rows
  contextPosition: number // Display position number (1, 2, 3...)
  questionType: QuestionType
  questionKind: FrontendQuestionKind
  entryPosition: number // For internal sorting
  responseOptions: FilterResponseOption[]
}

// Fork filter option (separate from questions)
export interface ForkFilterOption {
  id: string // Fork ID
  text: string // Fork name/label
  contextPosition: number
  questionKind: 'None' // Always 'None' for forks
  isMultipleChoice: false // Always false for forks
  entryPosition: number
  responseOptions: BranchOption[] // Fork branches, not response options
}

// Main API response
export interface FiltersResponse {
  filters: NewFilterOption[] // Question-based filters
  forkFilters: ForkFilterOption[] // Fork-based filters (separate list)
  totalFiltersCount: number // Total count for pagination
}

// Request body for POST /analytics/survey/{survey_id}/filters
export interface GetFiltersRequest {
  search?: string | null // Text search to filter questions
  offset?: number // Starting position for pagination (default: 0)
  limit?: number | null // Maximum number of filters to return
  questionIds?: string[] | null // Question UUIDs to include counts for
  responseFilters?: ResponseFilter[] | null // Active response filters
  forkFilters?: NewForkFilter[] | null // Active fork filters
}

// Active filters (for future implementation)
export interface ResponseFilter {
  questionId: string
  responseOptionIds: string[]
}

export interface NewForkFilter {
  forkId: string
  branchNumbers: number[]
}
